import * as React from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "./dialog";
import { cn } from "@/lib";

export interface ModalProps {
	trigger?: React.ReactNode;
	title?: React.ReactNode;
	description?: React.ReactNode;
	children?: React.ReactNode;
	footer?: React.ReactNode;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	contentClassName?: string;
	headerClassName?: string;
	footerClassName?: string;
	size?: "sm" | "md" | "lg" | "xl" | "full";
}

const sizeClasses = {
	sm: "max-w-sm",
	md: "max-w-md",
	lg: "max-w-lg",
	xl: "max-w-xl",
	full: "max-w-[95vw]",
};

export const Modal = React.forwardRef<HTMLDivElement, ModalProps>(
	(
		{
			trigger,
			title,
			description,
			children,
			footer,
			open,
			onOpenChange,
			contentClassName,
			headerClassName,
			footerClassName,
			size = "md",
		},
		ref
	) => {
		return (
			<Dialog open={open} onOpenChange={onOpenChange}>
				{trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
				<DialogContent
					ref={ref}
					aria-describedby={description ? "modal-description" : undefined}
					className={cn(sizeClasses[size], contentClassName)}
				>
					{(title || description) && (
						<DialogHeader className={cn("gap-4", headerClassName)}>
							{title && <DialogTitle>{title}</DialogTitle>}
							{description && (
								<DialogDescription>{description}</DialogDescription>
							)}
						</DialogHeader>
					)}
					{children}
					{footer && (
						<DialogFooter className={footerClassName}>{footer}</DialogFooter>
					)}
				</DialogContent>
			</Dialog>
		);
	}
);

Modal.displayName = "Modal";

// Example usage:
/*
<Modal
  trigger={<Button>Open Modal</Button>}
  title="Are you absolutely sure?"
  description="This action cannot be undone. This will permanently delete your account and remove your data from our servers."
  footer={
    <div className="flex gap-2">
      <Button variant="outline">Cancel</Button>
      <Button variant="destructive">Delete</Button>
    </div>
  }
  size="md"
>
  <div className="py-4">
    Additional content goes here
  </div>
</Modal>
*/
